const escHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const toMillis = (value: any): number => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return Number(value.toMillis() || 0);
  if (typeof value?.seconds === 'number') return Number(value.seconds || 0) * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return 0;
};

const toDateValue = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const text = String(value || '').trim();
    if (!text) return null;
    const ymd = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const dmy = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (dmy) {
      const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value?.toDate === 'function') {
    const dt = value.toDate();
    return dt instanceof Date ? dt : null;
  }
  const ms = toMillis(value);
  return ms > 0 ? new Date(ms) : null;
};

const toAmount = (value: any): number => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
};

const numberToWordsBelow1000 = (value: number): string => {
  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const n = Math.max(0, Math.floor(value));
  if (n < 20) return ones[n];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const r = n % 10;
    return `${tens[t]}${r ? ` ${ones[r]}` : ''}`.trim();
  }
  const h = Math.floor(n / 100);
  const r = n % 100;
  return `${ones[h]} Hundred${r ? ` ${numberToWordsBelow1000(r)}` : ''}`.trim();
};

export const amountToIndianWords = (value: number): string => {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num <= 0) return '';
  const rounded = Math.round(num * 100) / 100;
  const rupees = Math.floor(rounded);
  const paise = Math.round((rounded - rupees) * 100);
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const hundredPart = rupees % 1000;
  const parts: string[] = [];
  if (crore) parts.push(`${numberToWordsBelow1000(crore)} Crore`);
  if (lakh) parts.push(`${numberToWordsBelow1000(lakh)} Lakh`);
  if (thousand) parts.push(`${numberToWordsBelow1000(thousand)} Thousand`);
  if (hundredPart) parts.push(numberToWordsBelow1000(hundredPart));
  const rupeesWords = parts.join(' ').trim() || 'Zero';
  if (paise > 0) {
    return `Rupees ${rupeesWords} and ${numberToWordsBelow1000(paise)} Paise Only`;
  }
  return `Rupees ${rupeesWords} Only`;
};

const toReceiptCategory = (row: any): string =>
  String(row?.receiptCategory || '').trim().toLowerCase();
const toLegalChargeType = (row: any): string =>
  String(row?.legalChargeType || row?.chargeType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
const toTransactionDateValue = (row: any): Date | null =>
  toDateValue(row?.chqDate) ||
  toDateValue(row?.legacyChqDate) ||
  toDateValue(row?.txnDateYMD) ||
  toDateValue(row?.txnDate) ||
  toDateValue(row?.createdAt);
const toTransactionDateMillis = (row: any): number =>
  toTransactionDateValue(row)?.getTime() || Number.MAX_SAFE_INTEGER;
const CHECK_BADGE_HTML =
  '<span style="display:inline-flex;width:16px;height:16px;border-radius:50%;background:#16a34a;color:#fff;align-items:center;justify-content:center;font-size:11px;font-weight:700;line-height:1;vertical-align:middle;">✓</span>';

const renderRowsHtml = (rows: any[], formatDate: (value: Date) => string, formatAmount: (value: number) => string) => {
  if (!rows.length) {
    return `<tr style="height:11mm;"><td style="border:1px solid #bbb;padding:6px 8px;"></td><td style="border:1px solid #bbb;padding:6px 8px;"></td><td style="border:1px solid #bbb;padding:6px 8px;"></td><td style="border:1px solid #bbb;padding:6px 8px;"></td></tr>`;
  }
  return rows
    .map((row) => {
      const bankName = escHtml(String(row?.payerBankName || row?.bankName || ''));
      const chequeNo = escHtml(String(row?.referenceNo || ''));
      const dateObj = toTransactionDateValue(row);
      const dateLabel = dateObj ? escHtml(formatDate(dateObj)) : '';
      const amountLabel = escHtml(formatAmount(toAmount(row?.amount)));
      return `<tr style="height:11mm;"><td style="border:1px solid #bbb;padding:6px 8px;vertical-align:middle;">${bankName}</td><td style="border:1px solid #bbb;padding:6px 8px;vertical-align:middle;text-align:center;">${chequeNo}</td><td style="border:1px solid #bbb;padding:6px 8px;vertical-align:middle;text-align:center;">${dateLabel}</td><td style="border:1px solid #bbb;padding:6px 8px;vertical-align:middle;text-align:right;">₹ ${amountLabel}</td></tr>`;
    })
    .join('');
};

const buildRefundAmountByReceiptId = (transactions: any[]) => {
  const map = new Map<string, number>();
  (transactions || []).forEach((row) => {
    const isCustomerRefund =
      String(row?.txnType || '').toLowerCase() === 'payment' &&
      String(row?.paymentCategory || '').toLowerCase() === 'customer_refund';
    if (!isCustomerRefund) return;

    const totalAmount = toAmount(row?.amount);
    const allocations = Array.isArray(row?.refundAllocations) ? row.refundAllocations : [];
    if (allocations.length > 0) {
      allocations.forEach((allocation: any) => {
        const txnId = String(allocation?.txnId || '').trim();
        const amount = toAmount(allocation?.amount);
        if (!txnId || amount <= 0) return;
        map.set(txnId, (map.get(txnId) || 0) + amount);
      });
      return;
    }

    const ids = [
      String(row?.refundAgainstTxnId || '').trim(),
      ...(Array.isArray(row?.refundAgainstTxnIds) ? row.refundAgainstTxnIds.map((id: any) => String(id || '').trim()) : []),
    ].filter(Boolean);
    if (ids.length !== 1 || totalAmount <= 0) return;
    map.set(ids[0], (map.get(ids[0]) || 0) + totalAmount);
  });
  return map;
};

export const buildNetReceiptRows = (transactions: any[]) => {
  const refundedByReceiptId = buildRefundAmountByReceiptId(transactions || []);
  return (transactions || [])
    .filter((row) => String(row?.txnType || '').toLowerCase() === 'receipt')
    .map((row) => {
      const id = String(row?.id || '').trim();
      const amount = toAmount(row?.amount);
      const refunded = id ? Math.max(0, Number(refundedByReceiptId.get(id) || 0) || 0) : 0;
      const netAmount = Math.max(0, amount - refunded);
      if (refunded <= 0) return row;
      return {
        ...row,
        originalAmount: amount,
        refundedAmount: refunded,
        amount: netAmount,
      };
    })
    .filter((row) => toAmount(row?.amount) > 0);
};

export const buildPaymentTemplateTokens = (args: {
  transactions: any[];
  unitAmount: number;
  gstAmount: number;
  formatDate: (value: Date) => string;
  formatAmount: (value: number) => string;
  lineTokenLimit?: number;
}) => {
  const {
    transactions,
    unitAmount,
    gstAmount,
    formatDate,
    formatAmount,
    lineTokenLimit = 20,
  } = args;

  const receiptRows = buildNetReceiptRows(transactions || []);
  const sorted = [...receiptRows].sort((a, b) => {
    const aAt = toTransactionDateMillis(a);
    const bAt = toTransactionDateMillis(b);
    if (aAt !== bAt) return aAt - bAt;
    return String(a?.id || '').localeCompare(String(b?.id || ''));
  });

  const paymentRows = sorted.filter((row) => toReceiptCategory(row) === 'customer');
  const gstRows = sorted.filter((row) => toReceiptCategory(row) === 'gst');
  const chargeRows = (transactions || [])
    .filter((row) =>
      [
        'stamp_duty',
        'stamp',
        'registration',
        'registration_charge',
        'registration_charges',
        'agreement_fee',
        'sale_agreement_fee',
        'deed_fee',
        'sale_deed_fee',
      ].includes(toLegalChargeType(row))
    )
    .sort((a, b) => {
      const aAt = toTransactionDateMillis(a);
      const bAt = toTransactionDateMillis(b);
      if (aAt !== bAt) return aAt - bAt;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
  const summarizeCharge = (kind: 'stamp_duty' | 'registration_charges' | 'agreement_fee' | 'deed_fee') => {
    const aliases: Record<typeof kind, string[]> = {
      stamp_duty: ['stamp_duty', 'stamp'],
      registration_charges: ['registration_charges', 'registration_charge', 'registration'],
      agreement_fee: ['agreement_fee', 'sale_agreement_fee'],
      deed_fee: ['deed_fee', 'sale_deed_fee'],
    };
    const rows = chargeRows.filter((row) => aliases[kind].includes(toLegalChargeType(row)));
    const amount = rows.reduce((sum, row) => sum + toAmount(row?.amount), 0);
    const latest = rows.length ? rows[rows.length - 1] : null;
    const markedReceivedNoDetails = Boolean(
      latest?.receiptConfirmedWithoutDetails ||
      latest?.markReceivedNoDetails ||
      latest?.quickConfirmedReceipt
    );
    const date = toTransactionDateValue(latest);
    return {
      amount,
      amountText: amount > 0 ? formatAmount(amount) : '',
      chequeNo: markedReceivedNoDetails ? CHECK_BADGE_HTML : String(latest?.referenceNo || ''),
      dateText: markedReceivedNoDetails ? '-' : date ? formatDate(date) : '',
    };
  };
  const stampCharge = summarizeCharge('stamp_duty');
  const registrationCharge = summarizeCharge('registration_charges');
  const agreementCharge = summarizeCharge('agreement_fee');
  const deedCharge = summarizeCharge('deed_fee');
  const advocateFeeTotal = toAmount(agreementCharge.amount) + toAmount(deedCharge.amount);

  const payTotalValue = paymentRows.reduce((sum, row) => sum + toAmount(row?.amount), 0);
  const gstTotalValue = gstRows.reduce((sum, row) => sum + toAmount(row?.amount), 0);
  const payBalanceValue = Math.max(0, toAmount(unitAmount) - payTotalValue);
  const gstBalanceValue = Math.max(0, toAmount(gstAmount) - gstTotalValue);

  const firstPaymentRow = paymentRows[0] || null;
  const paymentSequenceNo = paymentRows.length > 0 ? paymentRows.length : 1;
  const firstPaymentDate = toTransactionDateValue(firstPaymentRow);

  const out: Record<string, string> = {
    '{{payments_rows}}': renderRowsHtml(paymentRows, formatDate, formatAmount),
    '{{gst_rows}}': renderRowsHtml(gstRows, formatDate, formatAmount),
    '{{pay_total}}': formatAmount(payTotalValue),
    '{{pay_balance}}': formatAmount(payBalanceValue),
    '{{gst_total}}': formatAmount(gstTotalValue),
    '{{gst_balance}}': formatAmount(gstBalanceValue),
    '{{chq_date}}': firstPaymentDate ? formatDate(firstPaymentDate) : '',
    '{{ref_chq_no}}': String(firstPaymentRow?.referenceNo || ''),
    '{{bank}}': String(firstPaymentRow?.payerBankName || firstPaymentRow?.bankName || ''),
    '{{deposit}}': formatAmount(toAmount(firstPaymentRow?.amount)),
    '{{deposit_in_words}}': amountToIndianWords(toAmount(firstPaymentRow?.amount)),
    '{{payment_sequence_no}}': String(paymentSequenceNo),
    '{{payment_no}}': String(paymentSequenceNo),
    '{{no_of_payment}}': String(paymentSequenceNo),
    '{{stamp_duty}}': stampCharge.amountText,
    '{{stamp_cheque_no}}': stampCharge.chequeNo,
    '{{stamp_date}}': stampCharge.dateText,
    '{{registration_charges}}': registrationCharge.amountText,
    '{{registration_cheque_no}}': registrationCharge.chequeNo,
    '{{registration_date}}': registrationCharge.dateText,
    '{{advocate_agreement_fee}}': agreementCharge.amountText,
    '{{advocate_deed_fee}}': deedCharge.amountText,
    '{{advocate_fee}}': advocateFeeTotal > 0 ? formatAmount(advocateFeeTotal) : '',
    '{{advocate_agreement_fee_cheque_no}}': agreementCharge.chequeNo,
    '{{advocate_agreement_fee_date}}': agreementCharge.dateText,
    '{{advocate_deed_fee_cheque_no}}': deedCharge.chequeNo,
    '{{advocate_deed_fee_date}}': deedCharge.dateText,
    '{{advocate_fee_cheque_no}}': agreementCharge.chequeNo || deedCharge.chequeNo,
    '{{advocate_fee_date}}': agreementCharge.dateText || deedCharge.dateText,
    '{{other_amount}}': '',
    '{{other_cheque_no}}': '',
    '{{other_date}}': '',
    '{{common_number}}': '',
    '{{applicant_number}}': '',
    '{{common_date}}': '',
    '{{applicant_date}}': '',
    '{{common_amount}}': '',
    '{{applicant_amount}}': '',
    '{{doc_serial_no}}': '',
  };

  for (let i = 1; i <= lineTokenLimit; i += 1) {
    const pay = paymentRows[i - 1] || null;
    const gst = gstRows[i - 1] || null;
    const payDate = toTransactionDateValue(pay);
    const gstDate = toTransactionDateValue(gst);

    out[`{{pay_bank_name_${i}}}`] = String(pay?.payerBankName || pay?.bankName || '');
    out[`{{pay_cheque_no_${i}}}`] = String(pay?.referenceNo || '');
    out[`{{pay_date_${i}}}`] = payDate ? formatDate(payDate) : '';
    out[`{{pay_amount_${i}}}`] = formatAmount(toAmount(pay?.amount));

    out[`{{gst_bank_name_${i}}}`] = String(gst?.payerBankName || gst?.bankName || '');
    out[`{{gst_cheque_no_${i}}}`] = String(gst?.referenceNo || '');
    out[`{{gst_date_${i}}}`] = gstDate ? formatDate(gstDate) : '';
    out[`{{gst_amount_${i}}}`] = formatAmount(toAmount(gst?.amount));
  }

  return out;
};
